import { useCallback, useEffect, useState } from 'react';

// Same storage key and behaviour as the main app's useTheme, minus the native bridge.
export type Theme = 'system' | 'light' | 'dark';
const STORAGE_KEY = 'theme-preference';

function systemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}
function stored(): Theme {
  try { return (localStorage.getItem(STORAGE_KEY) as Theme) || 'system'; } catch { return 'system'; }
}
function apply(theme: Theme) {
  const effective = theme === 'system' ? systemTheme() : theme;
  document.documentElement.classList.toggle('dark', effective === 'dark');
  return effective;
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(stored);
  const [effectiveTheme, setEffective] = useState<'light' | 'dark'>(() => apply(stored()));
  useEffect(() => {
    setEffective(apply(theme));
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => { if (theme === 'system') setEffective(apply('system')); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);
  const setTheme = useCallback((t: Theme) => {
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* private mode */ }
    setThemeState(t);
  }, []);
  return { theme, setTheme, effectiveTheme };
}
